import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import {
  FlaskConical,
  Heart,
  Activity,
  Sparkles,
  type LucideIcon,
} from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useFertilityTesting } from "../FertilityTestingContext";
import { getNextTestScreen } from "../getNextTestScreen";
import { useFertilityTestingUpload } from "../useFertilityTestingUpload";
import type { FertilityTestingStackParamList } from "../FertilityTestingNavigator";
import type {
  BloodTestCategory,
  TestResultCreate,
} from "@louise/medical-history/basic-info/types";

type BloodTestType = "hormonal" | "std" | "thyroid" | "other";

const bloodTests: {
  id: BloodTestType;
  labelKey: string;
  icon: LucideIcon;
}[] = [
  {
    id: "hormonal",
    labelKey: "med_history.fertility.testing.blood_type.hormonal",
    icon: FlaskConical,
  },
  {
    id: "std",
    labelKey: "med_history.fertility.testing.blood_type.std",
    icon: Heart,
  },
  {
    id: "thyroid",
    labelKey: "med_history.fertility.testing.blood_type.thyroid",
    icon: Activity,
  },
  {
    id: "other",
    labelKey: "med_history.fertility.testing.blood_type.other",
    icon: Sparkles,
  },
];

export function BloodTestsScreen() {
  const t = useTranslations();
  const { state, updateState } = useFertilityTesting();
  const navigation =
    useNavigation<NavigationProp<FertilityTestingStackParamList>>();
  const { uploadAction, uploadModal, uploadedFiles } =
    useFertilityTestingUpload();

  const bloodResults = state.testResults.filter((tr) => tr.type === "blood");
  const categoryToUI = useMemo<Record<string, BloodTestType>>(
    () => ({
      hormonal: "hormonal",
      std: "std",
      thyroid: "thyroid",
      other: "other",
    }),
    [],
  );

  const selected = useMemo<Set<BloodTestType>>(() => {
    const set = new Set<BloodTestType>();
    bloodResults.forEach((result) => {
      const uiType = categoryToUI[result.bloodTestCategory ?? ""];
      if (uiType) set.add(uiType);
    });
    return set;
  }, [bloodResults, categoryToUI]);

  const handleSelectionChange = (newSelection: BloodTestType[]) => {
    const categoryMap: Record<BloodTestType, BloodTestCategory> = {
      hormonal: "hormonal",
      std: "std",
      thyroid: "thyroid",
      other: "other",
    };
    const newSelectionSet = new Set(newSelection);

    const removedIds = bloodResults
      .filter((result) => {
        const uiType = categoryToUI[result.bloodTestCategory ?? ""];
        return result.id && uiType && !newSelectionSet.has(uiType);
      })
      .map((result) => result.id!);

    let updatedResults = state.testResults.filter((result) => {
      if (result.type !== "blood") return true;
      const uiType = categoryToUI[result.bloodTestCategory ?? ""];
      return uiType ? newSelectionSet.has(uiType) : true;
    });

    newSelection.forEach((uiType) => {
      if (!selected.has(uiType)) {
        const newTestResult: TestResultCreate = {
          type: "blood",
          bloodTestCategory: categoryMap[uiType],
        };
        updatedResults = [...updatedResults, newTestResult];
      }
    });

    updateState({
      testResults: updatedResults,
      testResultIdsToDelete: Array.from(
        new Set([...state.testResultIdsToDelete, ...removedIds]),
      ),
    });
  };

  const handleSave = () => {
    const newIndex = (state.currentNavigationIndex || 0) + 1;
    updateState({ currentNavigationIndex: newIndex });
    const nextScreen = getNextTestScreen({
      ...state,
      currentNavigationIndex: newIndex,
    });
    navigation.navigate(nextScreen as keyof FertilityTestingStackParamList);
  };

  const handleToggle = (id: BloodTestType) => {
    const nextSelection = new Set(selected);
    if (nextSelection.has(id)) {
      nextSelection.delete(id);
    } else {
      nextSelection.add(id);
    }
    handleSelectionChange(Array.from(nextSelection));
  };

  return (
    <>
      <QuestionLayout
        title={t("med_history.fertility.testing.blood_title")}
        subtitle={t("med_history.fertility.testing.blood_subtitle")}
        onBack={() => navigation.goBack()}
        onSave={handleSave}
        saveDisabled={selected.size === 0}
        saveLabel={t("med_history.fertility.continue")}
        secondaryAction={uploadAction}
        uploadedFiles={uploadedFiles}
      >
        <View style={styles.optionsGrid}>
          {bloodTests.map((test) => {
            const isSelected = selected.has(test.id);
            return (
              <View key={test.id} style={styles.optionWrapper}>
                <SquareOptionButton
                  icon={test.icon}
                  label={t(test.labelKey)}
                  isSelected={isSelected}
                  onPress={() => handleToggle(test.id)}
                  iconSize={20}
                  containerSize={40}
                  labelSize="sm"
                />
              </View>
            );
          })}
        </View>
      </QuestionLayout>
      {uploadModal}
    </>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
});

export default BloodTestsScreen;
